/// <reference lib="dom" />
import { gatewayAuthClient } from './gateway-auth.client';

export type OidcGatewayClient = 'web' | 'electron';

type ExchangeCodeParams = {
  code: string;
  codeVerifier: string;
  redirectUri: string;
  gatewayUrl: string;
  client: OidcGatewayClient;
};

type RefreshTokenParams = {
  gatewayUrl: string;
  client: OidcGatewayClient;
  refreshToken?: string;
};

type GetSessionParams = {
  gatewayUrl: string;
};

export async function getSessionFromGateway({ gatewayUrl }: GetSessionParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.getSession({
    overrideClientOptions: {
      baseUrl,
      credentials: 'include' as const,
    },
  });
}

export async function exchangeCodeWithGateway({
  code,
  codeVerifier,
  redirectUri,
  gatewayUrl,
  client,
}: ExchangeCodeParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.exchangeCode({
    body: {
      code,
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
    },
    ...(client === 'electron'
      ? { extraHeaders: { 'x-oidc-response': 'tokens' } as const }
      : {}),
    overrideClientOptions: {
      baseUrl,
      ...(client === 'web' ? { credentials: 'include' as const } : {}),
    },
  });
}

export async function refreshTokenWithGateway({
  gatewayUrl,
  client,
  refreshToken,
}: RefreshTokenParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.refreshToken({
    body: refreshToken ? { refresh_token: refreshToken } : {},
    ...(client === 'electron'
      ? { extraHeaders: { 'x-oidc-response': 'tokens' } as const }
      : {}),
    overrideClientOptions: {
      baseUrl,
      ...(client === 'web' ? { credentials: 'include' as const } : {}),
    },
  });
}

type LocalLoginParams = {
  gatewayUrl: string;
  username: string;
  password: string;
  client: OidcGatewayClient;
};

export async function loginWithGateway({
  gatewayUrl,
  username,
  password,
  client,
}: LocalLoginParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.localLogin({
    body: { username, password },
    ...(client === 'electron'
      ? { extraHeaders: { 'x-oidc-response': 'tokens' } as const }
      : {}),
    overrideClientOptions: {
      baseUrl,
      ...(client === 'web' ? { credentials: 'include' as const } : {}),
    },
  });
}

export async function loginLocalWebWithGateway({
  gatewayUrl,
  username,
  password,
}: Omit<LocalLoginParams, 'client'>) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.localLogin({
    body: { username, password },
    extraHeaders: { 'x-oidc-response': 'tokens' } as const,
    overrideClientOptions: {
      baseUrl,
      credentials: 'include' as const,
    },
  });
}

export async function getLocalSessionFromGateway({
  gatewayUrl,
}: GetSessionParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.localGetSession({
    overrideClientOptions: {
      baseUrl,
      credentials: 'include' as const,
    },
  });
}

export async function logoutLocalWithGateway({ gatewayUrl }: GetSessionParams) {
  const baseUrl = gatewayUrl.replace(/\/$/, '');
  return gatewayAuthClient.localLogout({
    overrideClientOptions: {
      baseUrl,
      credentials: 'include' as const,
    },
  });
}
